/* global google */
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Avatar, Collapse, Icon, List } from "antd";
import { useTranslation } from "react-i18next";
import { MAP } from "react-google-maps/lib/constants";
import useBreakpoint from "../../../../hooks/useBreackpoint";
import { VENUE_CATEGORY_LIST, getVenueCategoryLabel } from "../../../../services/venueCategories";
import Colors from "../../../../themes/Colors";

const PANEL_KEY = "categories";

// Fixed width so the control keeps its size whether it is open or closed.
// Map controls inherit Google's Roboto from .gm-style, so restore the app font.
const collapseStyle = {
  width: 160,
  margin: 10,
  background: Colors.snow,
  boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
  fontFamily: '"Poppins", sans-serif',
};
const listStyle = { maxHeight: "40vh", overflowY: "auto" };
const itemStyle = { padding: "2px 0" };
// Centers the small dot against the text (antd aligns meta to the top).
const metaStyle = { alignItems: "center" };
// antd fixes the meta title at 14px, so the size goes on the title itself.
const titleStyle = { fontSize: 12 };

// Map legend with the color and name of every venue category. Rendered
// as a native map control (same approach as DrawControlButton).
const VenueCategoryLegend = ({ map }) => {
  const { t } = useTranslation();
  const breakpoint = useBreakpoint();
  const [container, setContainer] = useState(null);
  // null = not toggled yet: starts collapsed on small screens only.
  const [collapsed, setCollapsed] = useState(null);

  useEffect(() => {
    const rawMap = map && map.context && map.context[MAP];
    if (!rawMap) return undefined;

    const controlDiv = document.createElement("div");
    // Left side, stacked above the zoom/street view controls (LEFT_BOTTOM too).
    const controls = rawMap.controls[google.maps.ControlPosition.LEFT_BOTTOM];
    controls.push(controlDiv);
    setContainer(controlDiv);

    return () => {
      const index = controls.getArray().indexOf(controlDiv);
      if (index > -1) controls.removeAt(index);
      setContainer(null);
    };
  }, [map]);

  if (!container) return null;

  const isCollapsed = collapsed === null ? ["xs", "sm"].includes(breakpoint) : collapsed;

  return ReactDOM.createPortal(
    <Collapse
      activeKey={isCollapsed ? [] : [PANEL_KEY]}
      onChange={(keys) => setCollapsed(!keys.length)}
      expandIconPosition="right"
      // Anchored at the bottom, so it opens upwards: up when closed, down when open.
      expandIcon={({ isActive }) => <Icon type="up" rotate={isActive ? 180 : 0} />}
      style={collapseStyle}
    >
      <Collapse.Panel header={t("Categories")} key={PANEL_KEY}>
        <List
          size="small"
          split={false}
          style={listStyle}
          dataSource={VENUE_CATEGORY_LIST}
          rowKey="color"
          renderItem={(category) => (
            <List.Item style={itemStyle}>
              <List.Item.Meta
                style={metaStyle}
                // Plain colored dot, no icon.
                avatar={<Avatar size={8} style={{ backgroundColor: category.color }} />}
                title={<span style={titleStyle}>{getVenueCategoryLabel(category, t)}</span>}
              />
            </List.Item>
          )}
        />
      </Collapse.Panel>
    </Collapse>,
    container
  );
};

export default VenueCategoryLegend;
